import { Activity, Layers, Network, ShieldCheck } from "lucide-react";
import { SolutionGroupContent } from "./types";

export const netwise: SolutionGroupContent = {
  key: "netwise",
  slug: "netwise",
  title: "NetWise",
  titleKey: "servicos.items.netwise.title",
  icon: Network,
  tagline: "Redes inteligentes e ambientes de alta disponibilidade para conectar cada unidade com segurança e desempenho.",
  description: [
    "Escolas, secretarias e empresas dependem de conexão estável para tudo: aulas on-line, sistemas de gestão, atendimento ao cidadão. O NetWise reúne as soluções de conectividade da Calriz, de redes corporativas e SD-WAN a data centers e ambientes em nuvem preparados para missão crítica.",
    "Projetamos, implantamos e acompanhamos a rede de ponta a ponta, com segurança desde a borda, capacidade para crescer e visibilidade completa do que acontece em cada link, equipamento e aplicação.",
  ],
  concepts: ["SD-WAN", "Data center", "Segurança", "Visibilidade"],
  items: [
    {
      key: "scalable",
      slug: "ambientes-escalaveis",
      title: "Ambientes Escaláveis",
      icon: Layers,
      tagline: "Infraestrutura que cresce junto com a demanda, sem quedas em picos de acesso e sem pagar por capacidade parada.",
      what: [
        "Um ambiente escalável é aquele que aumenta ou reduz sua capacidade conforme o uso: mais servidores, banda e processamento em períodos de matrícula, inscrição ou pagamento de tributos, e menos nos momentos tranquilos. Isso vale para a rede, para o data center e para as aplicações em nuvem.",
        "A Calriz combina links redundantes, balanceamento de carga, grupos de instâncias com escalonamento automático e arquiteturas híbridas entre data center local e Google Cloud, para que os serviços sigam no ar mesmo quando o acesso multiplica.",
      ],
      concepts: ["Balanceamento", "Autoescala", "Redundância", "Nuvem híbrida"],
      benefits: [
        { title: "Serviço sempre disponível", text: "Picos de acesso absorvidos sem lentidão nem indisponibilidade para o usuário." },
        { title: "Sem ponto único de falha", text: "Links, equipamentos e servidores redundantes que assumem automaticamente." },
        { title: "Custo proporcional ao uso", text: "A capacidade acompanha a demanda e deixa de ficar ociosa o ano inteiro." },
        { title: "Pronto para crescer", text: "Novas unidades, sistemas e usuários entram sem refazer a arquitetura." },
      ],
      steps: [
        { title: "Mapeamento de demanda", text: "Levantamos sazonalidade, gargalos atuais e metas de disponibilidade." },
        { title: "Arquitetura", text: "Desenhamos redundância, balanceamento e regras de escalonamento para cada camada." },
        { title: "Implantação", text: "Configuramos links, equipamentos e recursos em nuvem com testes de carga." },
        { title: "Acompanhamento", text: "Monitoramos o comportamento nos picos e ajustamos os limites com o tempo." },
      ],
      audience: ["Secretarias com serviços digitais ao cidadão", "Redes de ensino com muitas unidades", "Instituições com períodos de pico", "Empresas em expansão"],
    },
    {
      key: "secure_infra",
      slug: "infraestrutura-segura",
      title: "Infraestrutura Segura",
      icon: ShieldCheck,
      tagline: "Rede protegida da borda ao data center, com acesso controlado e tráfego inspecionado em cada ponto.",
      what: [
        "Infraestrutura segura é a rede desenhada para resistir a ataques e falhas desde a origem: firewalls de nova geração, segmentação entre áreas, acesso remoto com VPN ou modelo zero trust, Wi-Fi corporativo com autenticação por usuário e proteção contra ataques de negação de serviço (DDoS).",
        "Com SD-WAN, as unidades se conectam por vários links de forma inteligente e cifrada, e as políticas de segurança são aplicadas de forma central para todas as escolas, sedes ou filiais. No data center e na nuvem, seguimos o mesmo padrão, com regras versionadas e auditáveis.",
      ],
      concepts: ["Firewall NGFW", "Segmentação", "Zero trust", "SD-WAN"],
      benefits: [
        { title: "Superfície de ataque menor", text: "Segmentação impede que um incidente em uma área se espalhe pela rede inteira." },
        { title: "Políticas centralizadas", text: "Regras definidas uma vez e aplicadas em todas as unidades ao mesmo tempo." },
        { title: "Acesso de quem deve", text: "Autenticação por usuário e dispositivo na rede cabeada, no Wi-Fi e no acesso remoto." },
        { title: "Conformidade", text: "Registros e controles que apoiam a LGPD e as normas de segurança do setor público." },
      ],
      steps: [
        { title: "Avaliação", text: "Analisamos topologia, equipamentos, regras atuais e pontos de exposição." },
        { title: "Projeto", text: "Definimos segmentação, políticas de acesso, SD-WAN e proteção de borda." },
        { title: "Implantação", text: "Instalamos e configuramos os equipamentos com janela planejada e sem parar a operação." },
        { title: "Gestão contínua", text: "Atualizações, revisão de regras e resposta a alertas pela nossa equipe." },
      ],
      audience: ["Órgãos públicos e secretarias", "Redes de ensino com várias unidades", "Hospitais e serviços essenciais", "Empresas com filiais"],
    },
    {
      key: "observability",
      slug: "monitoramento-e-observabilidade",
      title: "Monitoramento e Observabilidade",
      icon: Activity,
      tagline: "Visão em tempo real de links, equipamentos e aplicações, para agir antes que o usuário perceba o problema.",
      what: [
        "Monitorar é acompanhar se cada link, switch, servidor e aplicação está funcionando. Observabilidade vai além: reúne métricas, logs e rastros para entender por que algo ficou lento ou caiu, e onde exatamente está a causa, seja na rede, na nuvem ou no sistema.",
        "A Calriz implanta painéis e alertas com ferramentas como Cloud Monitoring, Cloud Logging e as consoles de gestão de rede e SD-WAN, com indicadores de latência, perda de pacotes, uso de banda e disponibilidade por unidade, acompanhados pelo nosso NOC.",
      ],
      concepts: ["Métricas", "Logs", "Alertas", "Painéis"],
      benefits: [
        { title: "Problema resolvido antes", text: "Alertas avisam sobre degradação antes que vire indisponibilidade." },
        { title: "Causa encontrada rápido", text: "Métricas e logs correlacionados reduzem o tempo de diagnóstico." },
        { title: "SLA comprovado", text: "Relatórios de disponibilidade por link e unidade para cobrar operadoras e fornecedores." },
        { title: "Decisão com dados", text: "Histórico de uso mostra onde ampliar banda ou trocar equipamento." },
      ],
      steps: [
        { title: "Inventário", text: "Listamos links, equipamentos, servidores e aplicações críticas a monitorar." },
        { title: "Instrumentação", text: "Coletamos métricas e logs e definimos limites de alerta para cada item." },
        { title: "Painéis e alertas", text: "Montamos visões por unidade e por serviço e integramos os alertas ao atendimento." },
        { title: "Operação no NOC", text: "Nossa equipe acompanha os alertas, aciona responsáveis e entrega relatórios." },
      ],
      audience: ["Equipes de TI de secretarias e órgãos", "Redes de ensino com muitas escolas", "Empresas com operação distribuída", "Gestores que precisam comprovar SLA"],
    },
  ],
};
